/**
 * Контракт парсера источника. docs/phase-2/01-monitoring.md §4.2–4.3.
 *
 * Центральное требование Б2.2 — «пусто» отличается от «сломано» ТИПОМ, а не дисциплиной:
 * вернуть 'empty' можно только предъявив EmptyEvidence, то есть назвав контейнер, который
 * нашёлся и оказался пустым. «Ничего не нашлось» — это stub с 'container_missing'.
 * Код 200 не является доказательством ничего (ПРАВИЛО П-2): все три семейства заглушек
 * из sources.md приходят с кодом 200.
 */

import * as z from 'zod';
import type { FetchedResponse } from './fetcher.ts';

export const PARSER_KINDS = ['rss', 'html', 'tme_s'] as const;
export type ParserKind = (typeof PARSER_KINDS)[number];
// as const, не enum: type stripping не исполняет enum (PHASE-1.md §3.5).

export interface ParseContext {
  readonly sourceCode: string;
  readonly brandCode: string;
  /** Курсор источника, sources.md ПРАВИЛО 6.1. null — холодный старт. */
  readonly cursorAtMs: number | null;
  /** Часы прогона. Один и тот же now на весь прогон — иначе окно «плывёт» по ходу обхода. */
  readonly nowMs: number;
  readonly maxItems: number;
  /** Для t.me/s/: ожидаемый префикс data-post, он же имя канала. */
  readonly expectPrefix: string | null;
}

export interface ParsedItem {
  /** Абсолютный, разрешённый относительно base. До канонизации (§9.1). */
  readonly url: string;
  readonly guid: string | null;
  /** Уже декодированный: HTML-сущности сняты, теги вырезаны, пробелы схлопнуты. */
  readonly title: string;
  /** Тизер/лид из фида. null — поля не было; '' — поле было и оно пустое. Разница значима (§6.1.3). */
  readonly lead: string | null;
  /** Полный текст, если парсер его добыл сам (t.me/s/, MegaGroup). Иначе null → дозагрузка (§6.2). */
  readonly bodyText: string | null;
  /**
   * РАЗОБРАННАЯ дата источника. Именно разобранная, БЕЗ min(…, fetched_at):
   * зажим — свойство конвейера (sources.md ПРАВИЛО 6.9), а не парсера, и склеивать их в одном
   * месте значит потерять признак «дату разобрать не удалось» (§16.7).
   */
  readonly publishedAtMs: number | null;
  /** Как дата выглядела в источнике. Для разбора инцидентов и для тест-векторов. */
  readonly publishedAtRaw: string | null;
  readonly lang: string | null;
}

export interface EmptyEvidence {
  /**
   * ЧТО доказало, что мы смотрим на правильный документ: 'rss>channel', 'feed',
   * '.tgme_channel_info', 'article'. Пустой ответ без доказательства контейнера — не 'empty'.
   */
  readonly containerFound: string;
  readonly itemsFound: 0;
}

export const STUB_SIGNALS = [
  'redirect_lost_path', // t.me/s/x → t.me/x
  'content_type_not_feed', // Content-Type не XML на адресе фида
  'root_element_not_feed', // корень документа не rss/feed/RDF
  'channel_prefix_mismatch', // data-post указывает на чужой канал
  'body_below_min_length', // статья короче порога источника
  'anti_bot_markers', // «проверка браузера», challenge, meta refresh
  'charset_undecodable', // байты не декодируются ни как UTF-8, ни как заявлено
  'container_missing', // ни одного из ожидаемых контейнеров нет
] as const;
export type StubSignal = (typeof STUB_SIGNALS)[number];

export type ParseOutcome =
  | {
      readonly kind: 'items';
      readonly items: readonly ParsedItem[];
      /** Самый старый элемент документа. Для ПРАВИЛА 6.17 (переполнение фида). */
      readonly oldestItemAtMs: number | null;
      /** Токен следующей страницы (t.me/s/?before=N). null — глубже не пойдём. */
      readonly nextPageToken: string | null;
      /** Элементы, отброшенные самим парсером как неразбираемые. Не ноль — сигнал деградации. */
      readonly droppedItems: number;
      /**
       * Элементы, СРЕЗАННЫЕ потолком прогона (`ParseContext.maxItems`) с конца отсортированного
       * документа. Сегодня жёсткий литерал 500 на источник режет openai (1172 элемента в фиде) и
       * megagroup (529 замерено) КАЖДЫЙ прогон, и молча: наверх не доезжает ничего.
       *
       * ПОЧЕМУ ЭТО НЕ `droppedItems`. `droppedItems` означает «НЕРАЗБИРАЕМО» — разметка уехала,
       * дата не читается, элемент выброшен как испорченный, и не ноль здесь есть сигнал
       * ДЕГРАДАЦИИ источника. Обрезка потолком — противоположное: фид ЗДОРОВ настолько, что
       * элементов больше, чем мы согласились взять за прогон. Сложив их в одно число, мы бы
       * перевели самые живые источники канала в «деградирует» за одну ночь и обесценили бы
       * единственный признак настоящей поломки парсера.
       *
       * НЕОБЯЗАТЕЛЬНОЕ — и это условие, а не вкус: сериализованные исходы уже лежат фикстурами
       * корпуса, и обязательное поле разом перестало бы их разбирать. `undefined` и `0`
       * означают одно и то же — «не срезали»; ветвиться по разнице нечему.
       */
      readonly truncatedItems?: number | undefined;
    }
  | { readonly kind: 'empty'; readonly evidence: EmptyEvidence }
  | { readonly kind: 'not_this_parser'; readonly detail: string }
  | { readonly kind: 'stub'; readonly signal: StubSignal; readonly detail: string }
  | { readonly kind: 'malformed'; readonly detail: string };

export interface SourceParser {
  readonly kind: ParserKind;
  /**
   * Дешёвая проба по конверту: заголовки и первые байты, без разбора тела.
   * Возвращает ПРИЧИНУ отказа, а не булево — причина едет в source_fetch и в алерт.
   */
  accepts(
    res: FetchedResponse,
  ): { readonly ok: true } | { readonly ok: false; readonly reason: StubSignal };
  parse(res: FetchedResponse, ctx: ParseContext): Promise<ParseOutcome>;
}

/* ── zod-схемы: та же форма для валидации фикстур корпуса и сериализованных исходов ── */

export const ParseContextSchema = z.strictObject({
  sourceCode: z.string().min(1),
  brandCode: z.string().min(1),
  cursorAtMs: z.int().nullable(),
  nowMs: z.int(),
  maxItems: z.int().positive(),
  expectPrefix: z.string().nullable(),
});

export const ParsedItemSchema = z.strictObject({
  url: z.string().min(1),
  guid: z.string().nullable(),
  title: z.string(),
  lead: z.string().nullable(),
  bodyText: z.string().nullable(),
  publishedAtMs: z.int().nullable(),
  publishedAtRaw: z.string().nullable(),
  lang: z.string().nullable(),
});

/** itemsFound — литеральный 0: непустой контейнер — это исход 'items', а не «почти пусто». */
export const EmptyEvidenceSchema = z.strictObject({
  containerFound: z.string().min(1),
  itemsFound: z.literal(0),
});

export const ParseOutcomeSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('items'),
    items: z.array(ParsedItemSchema),
    oldestItemAtMs: z.int().nullable(),
    nextPageToken: z.string().nullable(),
    droppedItems: z.int().nonnegative(),
    /** Необязательное — иначе уже сериализованные фикстуры корпуса перестают разбираться. */
    truncatedItems: z.int().nonnegative().optional(),
  }),
  z.strictObject({ kind: z.literal('empty'), evidence: EmptyEvidenceSchema }),
  z.strictObject({ kind: z.literal('not_this_parser'), detail: z.string() }),
  z.strictObject({ kind: z.literal('stub'), signal: z.enum(STUB_SIGNALS), detail: z.string() }),
  z.strictObject({ kind: z.literal('malformed'), detail: z.string() }),
]);
